/**
 * Step 0/1 · Invite landing (ux-flows §2 steps 0–1) — the make-or-break screen: a stranger
 * deciding whether to do a favour on their phone. Order, per the founder-signed-off mock
 * (design-spec/reference/referee-flow.html, plate 01): who's asking → the ask, framed small →
 * the candidate's note → trust signals → Accept (coral) / a guilt-free Decline.
 *
 * Decline (§2 step 2) is a first-class action: tapping it opens a calm inline panel with OPTIONAL
 * neutral reason chips (never required) → confirm → the declined terminal. Accept mints the
 * cookie-bound session server-side and advances to Confirm. Both go through the resumable router.
 */
import { useState, type ReactNode } from 'react';
import { Button } from '@/components/ui/button';
import { ChoiceChips, type ChoiceOption } from '@/components/ui/choice-chips';
import { apiClient } from '@/lib/api-client';
import {
  DECLINE_REASON_LABELS,
  DECLINE_REASON_ORDER,
  type DeclineReasonToken,
} from '@/lib/api-types';
import { useAdvance } from '@/session/useAdvance';
import { useSession } from '@/session/useSession';

export function Landing() {
  const { token, context } = useSession();
  const { advance, pending, error } = useAdvance();
  const [declining, setDeclining] = useState(false);

  const candidate = context?.candidate;
  const firstName = candidate ? candidate.name.split(' ')[0] : null;
  const reasons = context?.declineReasons?.length ? context.declineReasons : DECLINE_REASON_ORDER;

  if (declining) {
    return (
      <DeclinePanel
        firstName={firstName}
        reasons={reasons}
        pending={pending}
        error={!!error}
        onCancel={() => setDeclining(false)}
        onConfirm={(reason) =>
          advance(() => apiClient.decline(token, reason ? { reason } : undefined), 'declined')
        }
      />
    );
  }

  return (
    <div className="flex flex-1 flex-col">
      {/* Who's asking — first (it materially lifts accept rates). */}
      {candidate && (
        <div className="flex items-center gap-3">
          <Avatar initials={candidate.initials} photoUrl={candidate.photoUrl} name={candidate.name} />
          <div className="min-w-0">
            <div className="font-display text-[14px] font-semibold text-ink">{candidate.name}</div>
            {candidate.headline && (
              <div className="truncate text-[11.5px] text-ink-quiet">{candidate.headline}</div>
            )}
          </div>
        </div>
      )}

      <h1 className="mt-4 font-display text-[24px] font-semibold leading-[1.15] tracking-[-0.02em] text-ink text-balance">
        {firstName ? `${firstName} asked for your word.` : 'A word, please.'}
      </h1>
      <p className="mt-2 font-body text-[14px] leading-relaxed text-ink-70 text-pretty">
        {firstName
          ? `Share a few minutes about working with ${firstName}. You approve every word before anything is sent — and it never takes more than three.`
          : 'You approve every word before anything is sent — and it never takes more than three.'}
      </p>

      {candidate?.note && (
        <figure className="mt-4 rounded-md border border-line bg-surface-tint p-3.5">
          <figcaption className="mb-1 font-display text-[9.5px] font-semibold uppercase tracking-[0.12em] text-ink-quiet">
            A note from {firstName}
          </figcaption>
          <blockquote className="font-body text-[12.5px] leading-relaxed text-ink">
            &ldquo;{candidate.note}&rdquo;
          </blockquote>
        </figure>
      )}

      <ul className="mt-4 flex flex-col gap-2.5">
        <TrustRow icon={<ShieldCheckIcon />}>You approve every word before anything is sent</TrustRow>
        <TrustRow icon={<LockIcon />}>
          We only use your details for this reference — never for anything else
        </TrustRow>
        <TrustRow icon={<ClockIcon />}>About three minutes, from your own phone</TrustRow>
      </ul>

      <details className="group mt-4 border-t border-line pt-3">
        <summary className="flex min-h-[44px] cursor-pointer list-none items-center justify-between font-display text-[12px] font-medium text-ink-quiet [&::-webkit-details-marker]:hidden">
          What is this?
          <ChevronIcon />
        </summary>
        <p className="pb-1 font-body text-[12.5px] leading-relaxed text-ink-70">
          By My Word turns a short voice note into a written reference.{' '}
          {firstName ? `${firstName} asked for your word` : 'Someone asked for your word'} — and
          only they ever see the finished letter, once you&rsquo;ve approved and signed it. We only
          use your details for this reference — never for anything else.{' '}
          <a
            href="https://bymyword.com/privacy"
            target="_blank"
            rel="noreferrer"
            className="font-medium text-coral-text underline-offset-2 hover:underline"
          >
            Privacy notice
          </a>
        </p>
      </details>

      <div className="mt-auto flex flex-col gap-3 pt-6">
        <Button
          variant="coral"
          size="block"
          disabled={pending || !context}
          onClick={() => advance(() => apiClient.accept(token), 'confirm')}
        >
          I&rsquo;m happy to help
        </Button>
        <Button variant="ghost" size="block" disabled={pending} onClick={() => setDeclining(true)}>
          I can&rsquo;t do this one
        </Button>
        {!!error && (
          <p role="alert" className="text-center text-[12px] font-medium text-coral-text">
            Something went wrong — please try again.
          </p>
        )}
      </div>
    </div>
  );
}

function DeclinePanel({
  firstName,
  reasons,
  pending,
  error,
  onCancel,
  onConfirm,
}: {
  firstName: string | null;
  reasons: DeclineReasonToken[];
  pending: boolean;
  error: boolean;
  onCancel: () => void;
  onConfirm: (reason: DeclineReasonToken | undefined) => void;
}) {
  const [reason, setReason] = useState<DeclineReasonToken | undefined>();
  const options: ChoiceOption<DeclineReasonToken>[] = reasons.map((value) => ({
    value,
    label: DECLINE_REASON_LABELS[value],
  }));

  return (
    <div className="flex flex-1 flex-col" aria-live="polite">
      <h1 className="font-display text-[22px] font-semibold leading-[1.15] tracking-[-0.02em] text-ink text-balance">
        No pressure at all.
      </h1>
      <p className="mt-2 font-body text-[14px] leading-relaxed text-ink-70 text-pretty">
        Declining is completely fine — you don&rsquo;t need a reason.{' '}
        {firstName ? `${firstName} will simply see that you couldn't this time.` : ''}
      </p>

      <div className="mt-5">
        <p className="mb-2 font-display text-[11px] font-medium text-ink-quiet">
          If you&rsquo;d like, a reason — it&rsquo;s optional:
        </p>
        <ChoiceChips
          legend="Reason for declining (optional)"
          options={options}
          value={reason}
          onChange={setReason}
          allowDeselect
        />
      </div>

      <div className="mt-auto flex flex-col gap-3 pt-6">
        <Button variant="coral" size="block" disabled={pending} onClick={() => onConfirm(reason)}>
          Send my reply
        </Button>
        <Button variant="ghost" size="block" disabled={pending} onClick={onCancel}>
          Actually, I&rsquo;ll help
        </Button>
        {error && (
          <p role="alert" className="text-center text-[12px] font-medium text-coral-text">
            Something went wrong — please try again.
          </p>
        )}
      </div>
    </div>
  );
}

function Avatar({ initials, photoUrl, name }: { initials: string; photoUrl?: string; name: string }) {
  if (photoUrl) {
    return <img src={photoUrl} alt={name} className="h-11 w-11 flex-none rounded-full object-cover" />;
  }
  return (
    <div
      aria-hidden="true"
      className="grid h-11 w-11 flex-none place-items-center rounded-full font-display text-[15px] font-semibold text-white"
      style={{ background: 'var(--grad-avatar-sage)' }}
    >
      {initials}
    </div>
  );
}

function TrustRow({ icon, children }: { icon: ReactNode; children: ReactNode }) {
  return (
    <li className="flex items-center gap-2.5 text-[12.5px] leading-snug text-ink">
      <span className="flex-none text-sage-deep">{icon}</span>
      {children}
    </li>
  );
}

/* --- inline icons (stroke SVG, no icon font) ------------------------------------------------ */

function ShieldCheckIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" className="h-4 w-4" aria-hidden="true">
      <path d="M12 2l7 4v6c0 5-3.5 8-7 10-3.5-2-7-5-7-10V6z" stroke="currentColor" strokeWidth={2} strokeLinejoin="round" />
      <path d="M9 12l2 2 4-4" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function LockIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" className="h-4 w-4" aria-hidden="true">
      <rect x="5" y="11" width="14" height="9" rx="2" stroke="currentColor" strokeWidth={2} />
      <path d="M8 11V8a4 4 0 018 0v3" stroke="currentColor" strokeWidth={2} />
    </svg>
  );
}

function ClockIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" className="h-4 w-4" aria-hidden="true">
      <circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth={2} />
      <path d="M12 8v4l3 2" stroke="currentColor" strokeWidth={2} strokeLinecap="round" />
    </svg>
  );
}

function ChevronIcon() {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      className="h-3.5 w-3.5 flex-none text-ink-52 transition-transform duration-ui ease-thread group-open:rotate-180"
      aria-hidden="true"
    >
      <path d="M6 9l6 6 6-6" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}
